import { useEffect, useRef, useState } from 'react'
import { Copy, Trash2 } from 'lucide-react'
import { cn } from '@/lib/cn'
import { packIntoLanes, type Spanned } from '../lanes'
import styles from '../styles/tags.module.scss'

/** One tag on the strip. Kept generic on purpose: captions are the first
 * user, speed ramps and freezes are the next, and they should all read and
 * behave the same rather than each growing its own bar. */
export interface Tag extends Spanned {
  label: string
  /** Accent for the bar; the caller owns what a colour means. */
  color: string
  /** What sort of effect this is. Purely presentational here — the strip
   * doesn't branch on it, it only lets a grade read as a different kind of
   * thing from a caption at a glance. */
  kind?: 'text' | 'grade'
}

export interface TagLanesProps {
  tags: Tag[]
  /** Timebase length, for converting seconds to percentages. */
  duration: number
  /** Visible window, so tags line up with a zoomed timeline. */
  view: { start: number; span: number }
  selectedId: string | null
  onSelect: (id: string | null) => void
  /** Fires throughout a drag — both edges, already clamped. */
  onChange: (id: string, start: number, end: number) => void
  /** Optional: when given, each tag reveals a duplicate button on hover.
   * Generic like the rest of this component — a speed ramp will want the
   * same affordance. */
  // Explicit ``| undefined``: the project runs with
  // ``exactOptionalPropertyTypes``, so a caller passing an optional
  // handler through has to be allowed to pass undefined.
  onDuplicate?: ((id: string) => void) | undefined
  onDelete?: ((id: string) => void) | undefined
  ariaLabel: string
}

/** Shortest span a tag can be dragged to. Below this the two edge handles
 * overlap and the tag can't be grabbed apart again. */
const MIN_SPAN_S = 0.2

type Drag = {
  id: string
  kind: 'move' | 'start' | 'end'
  originX: number
  originalStart: number
  originalEnd: number
}

/**
 * The strip of time-ranged tags under the timeline.
 *
 * Stacked downwards as they pile up (see ``packIntoLanes``), each one
 * draggable to move and resizable from either edge — the same gestures the
 * clip bars use, so there's one way to edit a span in this editor.
 */
export function TagLanes({
  tags,
  duration,
  view,
  selectedId,
  onSelect,
  onChange,
  onDuplicate,
  onDelete,
  ariaLabel,
}: TagLanesProps) {
  const stripRef = useRef<HTMLDivElement>(null)
  const [drag, setDrag] = useState<Drag | null>(null)

  const span = view.span > 0 ? view.span : duration
  const percentOf = (t: number) => (span > 0 ? ((t - view.start) / span) * 100 : 0)
  const { items, laneCount } = packIntoLanes(tags)

  useEffect(() => {
    if (!drag) return
    const el = stripRef.current
    if (!el) return
    const width = el.getBoundingClientRect().width
    if (width <= 0) return
    const perPixel = span / width

    const onMove = (e: MouseEvent) => {
      const delta = (e.clientX - drag.originX) * perPixel
      const length = drag.originalEnd - drag.originalStart

      if (drag.kind === 'move') {
        // Whole tag: preserve the length, clamp both ends inside the axis.
        const start = Math.max(0, Math.min(duration - length, drag.originalStart + delta))
        onChange(drag.id, start, start + length)
        return
      }
      if (drag.kind === 'start') {
        const start = Math.max(0, Math.min(drag.originalEnd - MIN_SPAN_S, drag.originalStart + delta))
        onChange(drag.id, start, drag.originalEnd)
        return
      }
      const end = Math.max(drag.originalStart + MIN_SPAN_S, Math.min(duration, drag.originalEnd + delta))
      onChange(drag.id, drag.originalStart, end)
    }
    const onUp = () => {
      setDrag(null)
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [drag, duration, span, onChange])

  const begin = (e: React.MouseEvent, kind: Drag['kind'], tag: Tag) => {
    if (e.button !== 0) return
    e.stopPropagation()
    e.preventDefault()
    onSelect(tag.id)
    setDrag({
      kind,
      id: tag.id,
      originX: e.clientX,
      originalStart: tag.start,
      originalEnd: tag.end,
    })
  }

  if (tags.length === 0) return null

  return (
    <div
      ref={stripRef}
      className={styles.strip}
      // Height follows the deepest stack so tags never spill or leave a
      // gap — the strip is exactly as tall as it needs to be.
      style={{ height: `${String(laneCount * LANE_H + (laneCount - 1) * LANE_GAP)}px` }}
      aria-label={ariaLabel}
      // Empty space between tags deselects, matching the video layer — one
      // rule for "clicking nothing means nothing is selected".
      role="presentation"
      onMouseDown={() => {
        onSelect(null)
      }}
    >
      {items.map(({ item: tag, lane }) => {
        const left = percentOf(tag.start)
        const width = percentOf(tag.end) - left
        const selected = tag.id === selectedId
        return (
          <div
            key={tag.id}
            className={cn(
              styles.tag,
              tag.kind === 'grade' && styles.tagGrade,
              selected && styles.tagSelected,
            )}
            style={{
              left: `${String(left)}%`,
              width: `${String(width)}%`,
              top: `${String(lane * (LANE_H + LANE_GAP))}px`,
              ['--tag-c' as string]: tag.color,
            }}
            onMouseDown={(e) => {
              begin(e, 'move', tag)
            }}
            role="button"
            tabIndex={0}
            aria-label={`${tag.label} — de ${tag.start.toFixed(1)}s a ${tag.end.toFixed(1)}s`}
            onKeyDown={(e) => {
              const step = e.shiftKey ? 1 : 0.1
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault()
                const delta = e.key === 'ArrowLeft' ? -step : step
                const length = tag.end - tag.start
                const start = Math.max(0, Math.min(duration - length, tag.start + delta))
                onSelect(tag.id)
                onChange(tag.id, start, start + length)
              }
            }}
          >
            <span
              className={styles.handle}
              onMouseDown={(e) => {
                begin(e, 'start', tag)
              }}
              role="presentation"
            />
            <span className={styles.tagLabel}>{tag.label}</span>
            {onDuplicate || onDelete ? (
              // Grouped so the two never straddle the label, and so a
              // narrow tag squeezes the label rather than the controls.
              <span className={styles.tagActions}>
                {onDuplicate ? (
                  <button
                    type="button"
                    className={styles.tagAction}
                    // Must not start a drag of the tag underneath.
                    onMouseDown={(e) => {
                      e.stopPropagation()
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      onDuplicate(tag.id)
                    }}
                    aria-label={`Duplicar ${tag.label}`}
                    title="Duplicar"
                  >
                    <Copy aria-hidden />
                  </button>
                ) : null}
                {onDelete ? (
                  <button
                    type="button"
                    className={cn(styles.tagAction, styles.tagActionDanger)}
                    onMouseDown={(e) => {
                      e.stopPropagation()
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete(tag.id)
                    }}
                    aria-label={`Excluir ${tag.label}`}
                    title="Excluir"
                  >
                    <Trash2 aria-hidden />
                  </button>
                ) : null}
              </span>
            ) : null}
            <span
              className={cn(styles.handle, styles.handleEnd)}
              onMouseDown={(e) => {
                begin(e, 'end', tag)
              }}
              role="presentation"
            />
          </div>
        )
      })}
    </div>
  )
}

/** Row height and spacing, in px. Mirrored in the stylesheet — kept here
 * too because the strip's height is computed, not laid out by flow. */
const LANE_H = 22
const LANE_GAP = 3
